// Base edge function client — typed invoke with error handling.
// Works in both FE (via supabaseClient) and Deno (if sb has functions.invoke).

import type { SupabaseClient } from '../base.ts';
import type { EdgeFnResponse, EdgeFnSuccess, EdgeFnError } from '../../api.ts';

export type { EdgeFnResponse, EdgeFnSuccess, EdgeFnError };

/** Raw envelope an edge function returns in `data` — validated at this boundary. */
interface EdgeFnEnvelope {
  success?: boolean;
  data?: unknown;
  error?: string;
  errors?: string[];
  code?: string;
  requiresReauth?: boolean;
  missingScopes?: string[];
}

/**
 * supabase-js resolves every non-2xx invoke with `{ data: null, error }` where
 * `error.message` is the fixed "Edge Function returned a non-2xx status code"
 * and the actual response body is only reachable via `error.context` (a
 * Response). Recover the envelope here — it is unreachable anywhere further
 * up, because the clients throw fresh Errors.
 */
async function readErrorBody(error: unknown): Promise<EdgeFnEnvelope | null> {
  const ctx = (error as { context?: { json?: () => Promise<unknown> } } | null)?.context;
  if (!ctx || typeof ctx.json !== 'function') return null;
  try {
    return ((await ctx.json()) as EdgeFnEnvelope | null) ?? null;
  } catch {
    return null;
  }
}

/** Error carrying the envelope's machine-readable fields for upstream handling
 *  (e.g. withGoogleAuth starts re-auth on `requiresReauth`). */
function envelopeError(message: string, envelope: EdgeFnEnvelope | EdgeFnError | null | undefined): Error {
  return Object.assign(new Error(message), {
    code: envelope?.code,
    requiresReauth: envelope?.requiresReauth,
    missingScopes: envelope?.missingScopes,
  });
}

/**
 * Minimal shape of the `functions.invoke` surface used by the edge-fn clients
 * (FE via supabase-js, Deno via the same client). `D` is the raw response body
 * the called function returns — each client declares its own and validates it.
 */
export interface FunctionsInvoker<D> {
  functions: {
    invoke: (
      fnName: string,
      opts: { body: Record<string, unknown> | FormData },
    ) => Promise<{ data: D | null; error: { message?: string } | null }>;
  };
}

export class BaseEdgeFnClient {
  constructor(protected readonly sb: SupabaseClient) {}

  /** Call an edge function, return typed response. */
  async call<T = unknown>(fnName: string, body: Record<string, unknown>): Promise<EdgeFnResponse<T>> {
    const { data, error } = await (this.sb as unknown as FunctionsInvoker<EdgeFnEnvelope>).functions.invoke(fnName, { body });
    if (error) {
      const envelope = await readErrorBody(error);
      return {
        success: false,
        error: envelope?.error || error.message || 'Edge function error',
        errors: envelope?.errors,
        code: envelope?.code,
        requiresReauth: envelope?.requiresReauth,
        missingScopes: envelope?.missingScopes,
      };
    }
    if (!data?.success) {
      return {
        success: false,
        error: data?.error || 'Unknown error',
        errors: data?.errors,
        code: data?.code,
        requiresReauth: data?.requiresReauth,
        missingScopes: data?.missingScopes,
      };
    }
    return { success: true, data: (data.data ?? data) as T };
  }

  /** Call and throw on failure — for void/data-only callers. */
  async callOrThrow<T = void>(fnName: string, body: Record<string, unknown>): Promise<T> {
    const result = await this.call<T>(fnName, body);
    if (!result.success) {
      throw envelopeError(result.errors?.join(', ') ?? result.error ?? 'Edge function failed', result);
    }
    return result.data;
  }

  /**
   * Call an edge function with file uploads via FormData.
   * Files are sent as binary (no base64 overhead). Metadata goes in a `_payload` JSON field.
   * Throws on failure — same contract as callOrThrow.
   */
  async callWithFiles<T = void>(
    fnName: string,
    payload: Record<string, unknown>,
    files: Record<string, File | Blob>,
  ): Promise<T> {
    const formData = new FormData();
    formData.append('_payload', JSON.stringify(payload));
    for (const [key, file] of Object.entries(files)) {
      formData.append(key, file);
    }
    const { data, error } = await (this.sb as unknown as FunctionsInvoker<EdgeFnEnvelope>).functions.invoke(fnName, {
      body: formData,
    });
    if (error) {
      const envelope = await readErrorBody(error);
      throw envelopeError(envelope?.error || error.message || 'Edge function error', envelope);
    }
    if (!data?.success) {
      throw envelopeError(data?.errors?.join(', ') ?? data?.error ?? 'Edge function failed', data);
    }
    return (data.data ?? data) as T;
  }
}
